import { Suspense, lazy, useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from '@/lib/AuthContext'
import { EnvironmentBanner, showEnvBanner } from '@/components/EnvironmentBanner'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary'
import {
  TelemetryPageTracker,
  TelemetrySync,
} from '@/components/TelemetryBridges'
import { LandingPage } from '@/pages/LandingPage'
import { SigninPage } from '@/pages/SigninPage'
import { SignupPage } from '@/pages/SignupPage'
import { VerifiedPage } from '@/pages/VerifiedPage'
import { ResetPasswordPage } from '@/pages/ResetPasswordPage'
import { UpdatePasswordPage } from '@/pages/UpdatePasswordPage'
import { IndPage } from '@/pages/IndPage'
import { LearnPage } from '@/pages/LearnPage'
import { PortfolioListPage } from '@/pages/PortfolioListPage'

/**
 * The two heavy signed-in screens are lazy so they stay out of the initial
 * bundle. Neither is reachable without an account, but every anonymous
 * visitor to the landing page was downloading both — and ExplorerPage pulls
 * in FullDocumentDiffViewer, the only importer of the `diff` package.
 *
 * PortfolioListPage is deliberately NOT lazy. It is ~2 kB, and it is what
 * `RootRoute` renders at `/` for a signed-in visitor. Lazy-loading it put a
 * Suspense fallback on the signed-in home page's first paint: AuthContext
 * settles with a plain `setState` (not inside a transition), so the
 * already-mounted boundary swaps in its fallback rather than holding the
 * previous UI, and the user saw a full-viewport white flash before the list
 * appeared. Splitting 2 kB is not worth that.
 *
 * These are named exports, hence the `.then` remap to a default export.
 */
const ExplorerPage = lazy(() =>
  import('@/pages/ExplorerPage').then((m) => ({ default: m.ExplorerPage })),
)
const ViewerPage = lazy(() =>
  import('@/pages/ViewerPage').then((m) => ({ default: m.ViewerPage })),
)

/**
 * Root route — branches on auth state, no redirect.
 *
 * Anonymous + loading visitors get the public landing page. Authenticated
 * users get the portfolio list. Same URL, same `index.html`, no client-side
 * `<Navigate>` — that previously bounced anonymous visitors to
 * `/auth/login`, which split the canonical URL (`/`) from where the
 * SEO content actually lived. Crawlers + canonical now agree.
 *
 * `loading` rendering matches the prerendered shell so there's no
 * visible flash before AuthContext settles.
 */
function RootRoute() {
  const { user, loading } = useAuth()
  if (loading || !user) return <LandingPage />
  return <PortfolioListPage />
}

// Reset scroll on every route change — BrowserRouter doesn't restore scroll,
// so clicking an IND card while scrolled down the homepage otherwise lands
// on /ind/{slug} at the old scroll offset (A2F-739 review feedback).
function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

export default function App() {
  return (
    <AuthProvider>
      <ScrollToTop />
      <EnvironmentBanner />
      <TelemetrySync />
      <TelemetryPageTracker />
      <div style={showEnvBanner ? { paddingTop: '32px' } : undefined}>
        {/* The boundary is OUTSIDE Suspense so it also catches a lazy chunk
            that fails to load — see RouteErrorBoundary for why that happens
            on a redeploy and why it would otherwise be a silent white page. */}
        <RouteErrorBoundary>
          {/* Explorer and Viewer are only reached by navigation, and
              react-router wraps navigation updates in startTransition, so
              this fallback holds the previous screen rather than flashing.
              It is a plain white block for the rare case it does show. */}
          <Suspense fallback={<div className="min-h-screen bg-white" />}>
            <Routes>
              {/* Public landing + signed-in app home — same URL, branches on auth */}
              <Route path="/" element={<RootRoute />} />

              {/* Public per-IND landing pages (A2F-739). Prerendered + own
                  JSON-LD; the document list itself stays auth-gated. */}
              <Route path="/ind/:slug" element={<IndPage />} />

              {/* Public long-form Learn articles. Prerendered + Article
                  JSON-LD; content authored in src/lib/learn.ts. */}
              <Route path="/learn/:slug" element={<LearnPage />} />

              {/* /auth/login is the standalone sign-in page. Old bookmarks and
                  the AuthContext deny-flash flow both land here correctly. */}
              <Route path="/auth/login" element={<SigninPage />} />

              {/* Unauthenticated auth flow pages */}
              <Route path="/auth/signup" element={<SignupPage />} />
              <Route path="/auth/verified" element={<VerifiedPage />} />
              <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
              <Route path="/auth/new-password" element={<UpdatePasswordPage />} />

              {/* Authenticated app */}
              <Route
                path="/portfolio/:portfolioId"
                element={
                  <ProtectedRoute>
                    <ExplorerPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/portfolio/:portfolioId/viewer"
                element={
                  <ProtectedRoute>
                    <ViewerPage />
                  </ProtectedRoute>
                }
              />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </RouteErrorBoundary>
      </div>
    </AuthProvider>
  )
}
